/**
 * Patient billing — the one implementation.
 *
 * ## Why this is in `shared/`
 *
 * The server must not believe whatever `total` and `balance` the browser
 * posts — a crafted request could then book any total against any set of
 * tests. The same arithmetic is also needed by `prisma/seed/patients.mts` and
 * the Calculator screen.
 *
 * So there is one function. The server derives the money it stores, the form
 * renders the same figures so nothing appears to change under the operator, and
 * the seed produces demo bills that agree with what the app would have charged.
 *
 * ## This file imports nothing, deliberately
 *
 * `prisma/seed/patients.mts` runs under Node's type stripping and imports its
 * dependencies with explicit `.ts` extensions. A relative import from here —
 * even `./schemas/enums` — is the kind of thing that typechecks and then fails
 * at runtime only when someone actually runs the seed, which is how a rename in
 * `prisma/seed` once shipped as a `ReferenceError`. The same rule already
 * governs `server/reports/report-rules.ts` and `patient-identifiers.ts`.
 *
 * The cost is that `PaymentStatus` is spelled out again below instead of being
 * imported. `server/prisma/enum-parity.ts` asserts the two agree, so the
 * duplicate cannot drift.
 *
 * ## Rounding
 *
 * The original `calcualtePrice` used `Math.floor`, discarding paise on every
 * discounted bill. Money is `Decimal(10,2)` in Postgres, so figures are rounded
 * to two decimals rather than truncated to whole rupees.
 */

/** Mirrors `PaymentStatus` in shared/schemas/enums.ts — see the note above. */
export type PricingPaymentStatus = 'paid' | 'partial' | 'pending';

/** Which discount the operator entered. The two are mutually exclusive. */
type DiscountKind = 'none' | 'percentage' | 'rupees';

interface PricingInput {
  discount_rupees: number;
  discount_percentage: number;
  paid: number;
  /**
   * Sales tax, as a percentage of what is actually being charged.
   *
   * Optional because two callers have no figure to offer and never will: the
   * spreadsheet importer, whose sheets predate the column, and the demo seed's
   * older cohorts. Absent means zero, which is what every visit registered
   * before this existed was charged.
   */
  tax_percentage?: number;
  /** A flat service fee — home collection, urgent processing, courier. */
  other_charges?: number;
}

export interface Pricing {
  subtotal: number;
  discount: number;
  /**
   * Subtotal less discount: the base tax is charged on.
   *
   * Named separately from `total` because once tax exists the two diverge, and
   * the registration slip needs both — it prints a discount line and a tax line
   * that must each be read from their own figure rather than inferred by
   * subtracting one printed number from another.
   */
  net: number;
  tax: number;
  other_charges: number;
  total: number;
  balance: number;
  payment_status: PricingPaymentStatus;
  discount_kind: DiscountKind;
}

/**
 * Two decimals, with the epsilon nudge that stops `1.005` rounding down.
 *
 * Not `toFixed(2)`: that returns a **string**, and a money column of strings
 * sorts lexically — 9 above 10 — while looking perfectly correct on screen.
 * Every call site would also have to parse it back before doing any further
 * arithmetic.
 *
 * Exported, and this is the only copy — the analytics services use it too, so
 * no reimplementation ever reaches for `toFixed`.
 * It lives here rather than in a `money.ts` of its own because this file
 * deliberately imports nothing — the demo seed loads it under Node's type
 * stripping, which cannot resolve an extensionless relative import — so a
 * separate module would be unreachable from exactly the code that needs it.
 */
export const round2 = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

/**
 * The bill for a set of test prices.
 *
 * `testPrices` rather than test ids: this file has no database. The caller
 * looks the prices up — the server from `LabTest.price`, the form from the
 * catalogue it already fetched — which is also what keeps the function pure.
 */
export function calculatePricing(input: PricingInput, testPrices: readonly number[]): Pricing {
  const subtotal = round2(testPrices.reduce((sum, price) => sum + price, 0));

  let net = subtotal;
  let discount_kind: DiscountKind = 'none';

  // Percentage wins when both are somehow set. The form clears one when the
  // other is entered, but a direct API call is under no such obligation, and
  // silently adding them together would be a discount nobody agreed to.
  if (input.discount_percentage > 0) {
    net = round2((subtotal * (100 - input.discount_percentage)) / 100);
    discount_kind = 'percentage';
  } else if (input.discount_rupees > 0) {
    net = round2(subtotal - input.discount_rupees);
    discount_kind = 'rupees';
  }

  // A discount larger than the bill must not produce a negative total.
  net = Math.max(0, net);

  // Tax is charged on the discounted amount, not the subtotal. Taxing money
  // that was never charged is wrong, and it is the order of operations on every
  // invoice a patient has ever been handed.
  const tax = round2((net * Math.max(0, input.tax_percentage ?? 0)) / 100);

  // Added after tax, and deliberately not taxed. `other_charges` is free-text
  // catch-all — home collection, urgent, courier — that the lab sets as a round
  // figure. Taxing it would apply a rate to a line nobody entered one for. If
  // that ever becomes wrong, it is one term in the expression below.
  const other_charges = round2(Math.max(0, input.other_charges ?? 0));

  const total = round2(net + tax + other_charges);
  const balance = round2(Math.max(0, total - input.paid));

  return {
    subtotal,
    discount: round2(subtotal - net),
    net,
    tax,
    other_charges,
    total,
    balance,
    payment_status: paymentStatusFor(total, input.paid),
    discount_kind,
  };
}

/**
 * The payment status implied by what is owed.
 *
 * Derived rather than chosen. A record marked "Paid" with Rs 500 outstanding is
 * exactly the kind of lie the worklist's status ladder already refuses, and it
 * poisons the outstanding-balance tile and the attention feed, both of which
 * filter on this column.
 *
 * A zero-total bill counts as paid: a fully discounted visit is settled, and
 * calling it "pending" would park it in the front desk's chase list forever.
 */
export function paymentStatusFor(total: number, paid: number): PricingPaymentStatus {
  if (paid >= total) return 'paid';
  return paid > 0 ? 'partial' : 'pending';
}
